import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Money } from '@principle-theorem/accounting';
import {
  AccClaimDialogComponent,
  AccInvoiceService,
  IAccClaimDialogData,
  IAccClaimDialogResult,
} from '@principle-theorem/ng-funding-requests';
import {
  AccountingFunctionsService,
  CurrentScopeFacade,
} from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import {
  AccClaimFundingRequest,
  getBaseTransaction,
  Invoice,
  resolveClaimItems,
  ServiceProviderHandler,
  toClaimableItems,
  Transaction,
} from '@principle-theorem/principle-core';
import {
  FundingRequestStatus,
  HealthcareClaimStatus,
  IAccClaimFundingRequest,
  IAccInvoiceFundingRequest,
  IClaimableItem,
  IHealthcareClaim,
  IInvoice,
  IPatient,
  IPractice,
  ITransaction,
  ServiceCodeType,
  TransactionAction,
  TransactionProvider,
  TransactionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  Firestore,
  isSameRef,
  multiSort,
  Region,
  snapshot,
  sortByCreatedAt,
  WithRef,
} from '@principle-theorem/shared';
import { sum, uniq, uniqWith } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ITransactionProvider,
  TransactionProviderType,
} from '../transaction-provider';

@Injectable()
export class AccTransactionProvider implements ITransactionProvider {
  private _invoiceService = inject(AccInvoiceService);
  private _dialog = inject(MatDialog);
  private _accountFunctions = inject(AccountingFunctionsService);
  private _currentScope = inject(CurrentScopeFacade);
  private _snackBar = inject(MatSnackBar);
  private _practice$ =
    this._currentScope.currentPractice$.pipe(filterUndefined());
  providerId = TransactionProvider.ACC;
  providerType = TransactionProviderType.ACC;
  providerRegions = [Region.NewZealand];
  isEnabled$ = this._practice$.pipe(
    map((practice) => !!practice?.accSettings?.enabled)
  );

  canCapture$(invoice: WithRef<IInvoice>): Observable<boolean> {
    return this._hasPendingAccTransaction$(invoice).pipe(
      map(
        (hasPendingAccTransaction) =>
          !hasPendingAccTransaction &&
          !Invoice.isPaid(invoice) &&
          Invoice.canAddTransactions(invoice)
      )
    );
  }

  async capture(
    invoice: WithRef<IInvoice>,
    claim: IHealthcareClaim
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const patient = await Firestore.getDoc(Invoice.patientRef(invoice));
    const claimItems = this._getFilteredClaimItems(invoice, claim);
    const staffer = await Invoice.findInvoicePractitioner(invoice);
    const practice = await snapshot(this._practice$);
    const accClaims = await this._resolveValidAccClaims(patient, practice);
    const result = await this._dialog
      .open<
        AccClaimDialogComponent,
        IAccClaimDialogData,
        IAccClaimDialogResult
      >(
        AccClaimDialogComponent,
        DialogPresets.medium({
          data: {
            accClaims,
            patient,
            practice,
            staffer,
          },
        })
      )
      .afterClosed()
      .toPromise();
    if (!result) {
      return;
    }

    const invoiceRequest = await this._invoiceService.generateInvoice(
      result.claimData,
      claimItems,
      claim,
      invoice,
      patient
    );
    if (!invoiceRequest) {
      return;
    }

    const transactionRef = await this._addTransaction(
      invoice,
      claim,
      claimItems
    );
    if (!transactionRef) {
      return;
    }

    await this._linkTransactionToInvoiceRequest(
      invoiceRequest,
      transactionRef,
      invoice.ref
    );
    if (result.claimRequest) {
      await this._linkInvoiceToClaimRequest(result.claimRequest, invoice);
    }

    this._snackBar.open('Added Pending ACC Transaction');
  }

  private _getFilteredClaimItems(
    invoice: WithRef<IInvoice>,
    claim: IHealthcareClaim
  ): IClaimableItem[] {
    return toClaimableItems(resolveClaimItems(invoice, claim)).filter(
      (item) => {
        const serviceCode = ServiceProviderHandler.findServiceCode(
          item.serviceCode.code
        );
        return serviceCode?.type === ServiceCodeType.ACC;
      }
    );
  }

  private async _addTransaction(
    invoice: WithRef<IInvoice>,
    claim: IHealthcareClaim,
    claimItems: IClaimableItem[]
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const baseTransaction = await getBaseTransaction(invoice);
    const amount = Money.amount(
      sum(claimItems.map((item) => item.serviceCode.amount * item.quantity))
    );
    const transaction = Transaction.init({
      ...baseTransaction,
      ...Transaction.internalReference(TransactionProvider.ACC),
      status: TransactionStatus.Pending,
      amount,
      attributedTo: claim.practitioner.ref,
      practiceRef: invoice.practice.ref,
    });

    return this._accountFunctions.addTransactionToInvoice(
      invoice,
      transaction,
      TransactionAction.Add,
      claim
    );
  }

  private async _linkTransactionToInvoiceRequest(
    invoiceRequest: WithRef<IAccInvoiceFundingRequest>,
    transactionRef: DocumentReference<ITransaction>,
    invoiceRef: DocumentReference<IInvoice>
  ): Promise<void> {
    const transactions = uniqWith(
      [...(invoiceRequest.data.transactions || []), transactionRef],
      isSameRef
    );

    await Firestore.patchDoc(invoiceRequest.ref, {
      data: {
        ...invoiceRequest.data,
        transactions,
        invoiceRef,
      },
    });
  }

  private async _linkInvoiceToClaimRequest(
    claimRequest: WithRef<IAccClaimFundingRequest>,
    invoice: WithRef<IInvoice>
  ): Promise<void> {
    const associatedInvoiceRefs = claimRequest.data.invoiceRefs || [];
    await Firestore.patchDoc(claimRequest.ref, {
      data: {
        ...claimRequest.data,
        invoiceRefs: uniq([...associatedInvoiceRefs, invoice.ref]),
      },
    });
  }

  private _hasPendingAccTransaction$(
    invoice: WithRef<IInvoice>
  ): Observable<boolean> {
    return Invoice.transactions$(invoice).pipe(
      map((transactions) =>
        transactions.some((transaction) =>
          this._isPendingAccTransaction(transaction, invoice)
        )
      )
    );
  }

  private _isPendingAccTransaction(
    transaction: WithRef<ITransaction>,
    invoice: WithRef<IInvoice>
  ): boolean {
    const hasMatchingClaimTransaction = !!invoice.claims?.some(
      (claim) =>
        claim.status === HealthcareClaimStatus.Claimed &&
        claim.transactions.some((claimTransaction) =>
          isSameRef(claimTransaction, transaction)
        )
    );
    return (
      transaction.status === TransactionStatus.Pending &&
      transaction.provider === TransactionProvider.ACC &&
      hasMatchingClaimTransaction
    );
  }

  private async _resolveValidAccClaims(
    patient: WithRef<IPatient>,
    practice: WithRef<IPractice>
  ): Promise<WithRef<IAccClaimFundingRequest>[]> {
    const allClaims = await snapshot(
      AccClaimFundingRequest.byPatient$(practice, patient).pipe(
        multiSort(sortByCreatedAt)
      )
    );
    const testMode = !!practice.accSettings?.testMode;

    return allClaims.filter((claim) => {
      const hasClaimNumber = !!claim.data.claimData.claimNumber;
      const matchesTestMode = !!claim.data.testMode === testMode;
      const submittedOrApproved = [
        FundingRequestStatus.Approved,
        FundingRequestStatus.Submitted,
        FundingRequestStatus.Pending,
      ].includes(claim.status);

      return hasClaimNumber && submittedOrApproved && matchesTestMode;
    });
  }
}
